import React, { useEffect, useState, useCallback } from "react";
import "../styles/AdminMatrimonial.css";
import { MATRIMONIAL_ENQUIRY_API } from "../config/api";
import { showConfirm } from "../utils/customDialog";

function AdminMatrimonial() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  /* ================= POPUP ================= */
  const showPopup = (msg) => {
    const div = document.createElement("div");
    div.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #5d1d2b;
        color: #fff;
        padding: 14px 18px;
        border-radius: 10px;
        z-index: 99999;
        box-shadow: 0 10px 25px rgba(93, 29, 43, 0.25);
        font-size: 14px;
        font-weight: 600;
        max-width: 300px;
        animation: slideIn 0.3s ease;
      ">
        ${msg}
      </div>
      <style>
        @keyframes slideIn {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      </style>
    `;
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 2500);
  };

  /* ================= COPY HELPER ================= */
  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    showPopup(`📋 Copied ${label} to clipboard!`);
  };

  /* ================= FETCH ================= */
  const fetchEntries = useCallback(async () => {
    try {
      const res = await fetch(MATRIMONIAL_ENQUIRY_API);
      const data = await res.json();

      if (data?.status === "success") {
        setEntries(Array.isArray(data.data) ? data.data : []);
      } else {
        setEntries([]);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ================= INIT ================= */
  useEffect(() => {
    fetchEntries();
    const interval = setInterval(fetchEntries, 5000);
    return () => clearInterval(interval);
  }, [fetchEntries]);

  /* ================= DELETE ================= */
  const deleteEntry = async (id) => {
    const ok = await showConfirm({
      title: "Delete Matrimonial Enquiry",
      message: "Are you sure you want to delete this matrimonial enquiry?",
      subMessage: "This will permanently delete the enquiry record.",
      confirmText: "Delete Enquiry",
      type: "danger"
    });
    if (!ok) return;

    try {
      const res = await fetch(MATRIMONIAL_ENQUIRY_API, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });

      const result = await res.json();
      if (result?.status === "success") {
        setEntries((prev) => prev.filter((item) => item.id !== id));
        showPopup("🗑 Enquiry deleted successfully");
      } else {
        showPopup("❌ Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showPopup("❌ Server error");
    }
  };

  // Get Initials for Avatar
  const getInitials = (name) => {
    if (!name) return "M";
    const parts = name.trim().split(" ");
    if (parts.length > 1) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  // Filtered Entries
  const filteredEntries = entries.filter((item) => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    return (
      (item.name || "").toLowerCase().includes(query) ||
      (item.email || "").toLowerCase().includes(query) ||
      (item.phone || "").toLowerCase().includes(query) ||
      (item.message || "").toLowerCase().includes(query)
    );
  });

  /* ================= UI ================= */
  return (
    <div className="admin-container matrimonial-admin-container">
      
      {/* Header */}
      <div className="matrimonial-header">
        <div className="matrimonial-header-left">
          <h2>
            <i className="bi bi-envelope-heart"></i> Matrimonial Advertisement Enquiries
          </h2>
          <p>Review and manage all incoming matrimonial ad requests from the website</p>
        </div>
        <div className="matrimonial-header-right">
          <div className="matrimonial-stat-badge">
            Total Enquiries: <span className="count">{entries.length}</span>
          </div>
        </div>
      </div>

      {/* Search Filter */}
      <div className="search-box-wrapper">
        <i className="bi bi-search search-icon"></i>
        <input
          type="text"
          className="search-input admin-search-input"
          style={{ paddingLeft: "48px" }}
          placeholder="Search by name, email, phone, or message..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="matrimonial-loading">
          <div className="spinner-border text-primary" role="status"></div>
          <p>Loading enquiries...</p>
        </div>
      ) : (
        <div className="enquiries-card-grid">
          {filteredEntries.length === 0 ? (
            <div className="no-results-card">
              <i className="bi bi-folder-x"></i>
              <h3>No enquiries found</h3>
              <p>Try refining your search query or check back later.</p>
            </div>
          ) : (
            filteredEntries.map((item) => (
              <div key={item.id} className="enquiry-card">
                
                {/* Card Header */}
                <div className="enquiry-card-header">
                  <div className="avatar-initials">{getInitials(item.name)}</div>
                  <div className="header-meta">
                    <h3 className="meta-name">{item.name}</h3>
                    <p className="meta-date">
                      <i className="bi bi-calendar3"></i> {new Date(item.created_at).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </p>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="enquiry-contact-info">
                  <div className="contact-row">
                    <span className="contact-label">
                      <i className="bi bi-envelope"></i> Email:
                    </span>
                    <span className="contact-value">
                      {item.email}
                      <button
                        className="copy-btn"
                        onClick={() => handleCopy(item.email, "email")}
                        title="Copy Email"
                      >
                        <i className="bi bi-clipboard"></i>
                      </button>
                    </span>
                  </div>
                  <div className="contact-row">
                    <span className="contact-label">
                      <i className="bi bi-telephone"></i> Phone:
                    </span>
                    <span className="contact-value">
                      {item.phone}
                      <button
                        className="copy-btn"
                        onClick={() => handleCopy(item.phone, "phone")}
                        title="Copy Phone"
                      >
                        <i className="bi bi-clipboard"></i>
                      </button>
                    </span>
                  </div>
                </div>

                {/* Enquiry Message */}
                <div className="enquiry-message-box">
                  <div className="message-label">Enquiry Message</div>
                  <div className="message-content">{item.message}</div>
                </div>

                {/* Card Actions */}
                <div className="card-actions-row">
                  <button
                    className="btn-delete-enquiry"
                    onClick={() => deleteEntry(item.id)}
                  >
                    <i className="bi bi-trash3"></i> Delete Enquiry
                  </button>
                </div>

              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default AdminMatrimonial;

